// @flow
import { of } from 'rxjs';
import update from 'immutability-helper';
import { ofType } from 'redux-observable';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { getPunchCardPoints, postPunchCardClaim } from './api';
import { actions as AuthActions } from '../auth/duck';
import { actions as CheckoutActions } from '../new-order/CheckoutStep/duck';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';

export type PunchCardAction = {
  +type: string;
  +count?: number;
  +total?: number;
  +coupon?: string;
  +fetchPointsError?: HTTPError;
  +claimPointsError?: HTTPError;
};

export type PunchCardState = {
  +isFetchingPoints?: boolean;
  +count?: number;
  +total?: number;
  +isClaimingPoints?: boolean;
  +coupon?: string;
  +fetchPointsError?: HTTPError;
  +claimPointsError?: HTTPError;
};

// Acions
const PUNCH_CARD_RESET_STATE = 'PUNCH_CARD_RESET_STATE';

const FETCH_PUNCH_CARD_REQUEST = 'FETCH_PUNCH_CARD_REQUEST';
const FETCH_PUNCH_CARD_SUCCESS = 'FETCH_PUNCH_CARD_SUCCESS';
const FETCH_PUNCH_CARD_ERROR = 'FETCH_PUNCH_CARD_ERROR';

const CLAIM_PUNCH_CARD_REQUEST = 'CLAIM_PUNCH_CARD_REQUEST';
const CLAIM_PUNCH_CARD_SUCCESS = 'CLAIM_PUNCH_CARD_SUCCESS';
const CLAIM_PUNCH_CARD_ERROR = 'CLAIM_PUNCH_CARD_ERROR';

// Action creators
export function resetPunchCardState() {
  return {
    type: PUNCH_CARD_RESET_STATE,
  };
}

export function fetchPunchCardPoints() {
  return {
    type: FETCH_PUNCH_CARD_REQUEST,
  }
}

export function fetchPunchCardPointsSuccess(count: number, total: number) {
  return {
    type: FETCH_PUNCH_CARD_SUCCESS,
    count,
    total,
  }
}

export function fetchPunchCardPointsError(error: HTTPError) {
  return {
    type: FETCH_PUNCH_CARD_ERROR,
    error,
  };
}

export function claim() {
  return {
    type: CLAIM_PUNCH_CARD_REQUEST,
  };
}

export function claimSuccess(coupon: string) {
  return {
    type: CLAIM_PUNCH_CARD_SUCCESS,
    coupon,
  };
}

export function claimError(claimPointsError: HTTPError) {
  return {
    type: CLAIM_PUNCH_CARD_ERROR,
    claimPointsError,
  };
}

export const actions = {
  PUNCH_CARD_RESET_STATE,
  FETCH_PUNCH_CARD_REQUEST,
  FETCH_PUNCH_CARD_SUCCESS,
  FETCH_PUNCH_CARD_ERROR,
  CLAIM_PUNCH_CARD_REQUEST,
  CLAIM_PUNCH_CARD_SUCCESS,
  CLAIM_PUNCH_CARD_ERROR,
  resetPunchCardState,
  fetchPunchCardPoints,
  fetchPunchCardPointsSuccess,
  fetchPunchCardPointsError,
  claim,
  claimSuccess,
  claimError,
};

export const initialState: PunchCardState = {};

export default function reducer(state: PunchCardState = initialState, action: PunchCardAction): PunchCardState {
  switch (action.type) {
    case FETCH_PUNCH_CARD_REQUEST:
      return update(state, {
        isFetchingPoints: { $set: true },
      });
    case FETCH_PUNCH_CARD_SUCCESS:
      return update(state, {
        count: { $set: action.count },
        total: { $set: action.total },
      });
    case FETCH_PUNCH_CARD_ERROR:
      return update(state, {
        fetchPointsError: { $set: action.fetchPointsError },
      });
    case CLAIM_PUNCH_CARD_REQUEST:
      return update(state, {
        isClaimingPoints: { $set: true },
      });
    case CLAIM_PUNCH_CARD_SUCCESS:
      return update(state, {
        $unset: ['isClaimingPoints'],
        coupon: { $set: action.coupon },
        // NOTE: we deleted count here because we are fetching it whenever a success claim happens
        // count: { $set: 0 },
      });
    case CLAIM_PUNCH_CARD_ERROR:
      return update(state, {
        $unset: ['isClaimingPoints'],
        claimPointsError: { $set: action.claimPointsError },
      });
    case PUNCH_CARD_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

export function fetchPunchCardPointsEpic(action$: Observable<PunchCardAction>): Observable<PunchCardAction> {
  return action$.pipe(
    ofType(
      AuthActions.AUTH_LOGIN_SUCCESS,
      AuthActions.AUTH_REGISTER_SUCCESS,
      CheckoutActions.CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS,
      FETCH_PUNCH_CARD_REQUEST,
      CLAIM_PUNCH_CARD_SUCCESS),
    mergeMap(() => getPunchCardPoints().pipe(
      map(({ count, total }) => {
        return fetchPunchCardPointsSuccess(count, total);
      }),
      catchError((e: HTTPError) => of(fetchPunchCardPointsError(e))))
    ));
}

export function claimPunchCardPointsEpic(action$: Observable<PunchCardAction>): Observable<PunchCardAction> {
  return action$.pipe(
    ofType(CLAIM_PUNCH_CARD_REQUEST),
    mergeMap(() => postPunchCardClaim().pipe(
      map(({ couponCode }) => {
        return claimSuccess(couponCode);
      }),
      catchError((e: HTTPError) => of(claimError(e))))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/punch-card/duck.js